import './media-gallery.scss';
import {Box, Typography, useMediaQuery} from '@mui/material';
import {CancelToken} from 'axios';
import {MediaGalleryType} from '../../enums/media-gallery-type';
import {useInfiniteLoading} from '../../hooks/infinite-loading';
import {EntityMediaRepository} from '../../repositories/entity-media';
import {ContentPage} from '../content-page/content-page';
import {LoadingContent} from '../loading-content/loading-content';
import {EntityMedia} from '../../backend/interfaces/entity-media';
import {memo, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';

const MediaItem = memo((props: {media: EntityMedia; linkUrl: string; alt?: string}) => {
  return (
    <Link
      className="media-gallery-media-item-link"
      to={{
        pathname: props.linkUrl,
        state: {
          entityMedia: props.media,
        },
      }}
    >
      <Box className="media-gallery-media-item-img-container">
        <img className="media-gallery-media-item-img" src={props.media.url} alt={props.alt || ''} />
      </Box>
    </Link>
  );
});

/**
 * Media gallery component.
 *
 * Note: This component only works for the entity media gallery but in the future we
 * will extend it to include the seniors' gallery for the informal caregiver area.
 */
export const MediaGallery = (props: {
  type: MediaGalleryType;
  baseMediaUrl: string;
  entityId?: string;
}) => {
  // Hold state
  const [columns, setColumns] = useState<{
    firstColumn: Array<EntityMedia>;
    secondColumn: Array<EntityMedia>;
    thirdColumn: Array<EntityMedia>;
  }>({
    firstColumn: [],
    secondColumn: [],
    thirdColumn: [],
  });

  // Media query
  const mediumDevicesMediaQueryMatches: boolean = useMediaQuery('(max-width:800px)');
  const smallDevicesMediaQueryMatches: boolean = useMediaQuery('(max-width:500px)');

  // Translations
  const {t} = useTranslation();

  // Fetch media
  const handleFetchData = async (
    limit: number,
    skip: number,
    cancelToken?: CancelToken
  ): Promise<{
    totalCount: number;
    data: Array<{
      id: string;
      item: EntityMedia;
    }>;
  }> => {
    try {
      if (props.type === MediaGalleryType.EntityMedia && props.entityId) {
        // Get entity media
        const results = await EntityMediaRepository.instance.getInEntity(
          props.entityId,
          limit,
          skip,
          cancelToken
        );

        // Return
        return {
          totalCount: results.totalCount,
          data: results.data.map((media) => {
            return {
              id: media.id || '',
              item: media,
            };
          }),
        };
      } else {
        // Error
        console.debug('🔴 Could not fetch media the data is invalid.', {
          ...props,
        });

        // Return
        return {
          totalCount: 0,
          data: [],
        };
      }
    } catch (error) {
      // Error
      console.debug('🔴 Could not fetch media.', error);

      // Return
      return {
        totalCount: 0,
        data: [],
      };
    }
  };

  // Infinite loading hook
  const {isLoading, data, allLoaded} = useInfiniteLoading({
    handleFetchData,
    type: 'standard',
  });

  // Update the columns when the data or the screen width update
  useEffect(() => {
    const separateData = () => {
      // Columns
      let firstColumn: Array<EntityMedia> = [];
      let secondColumn: Array<EntityMedia> = [];
      let thirdColumn: Array<EntityMedia> = [];

      // Separate into columns according to the screen width
      if (smallDevicesMediaQueryMatches) {
        // Small screens - 1 column
        firstColumn = [...data];
      } else if (!smallDevicesMediaQueryMatches && mediumDevicesMediaQueryMatches) {
        // Medium screens - 2 columns
        for (let i = 0; i < data.length; i++) {
          if (i === 0 || i % 2 === 0) {
            firstColumn.push(data[i]);
          } else {
            secondColumn.push(data[i]);
          }
        }
      } else {
        // Large screens - 3 columns
        for (let i = 0; i < data.length; i += 3) {
          firstColumn.push(data[i]);
        }
        for (let i = 1; i < data.length; i += 3) {
          secondColumn.push(data[i]);
        }
        for (let i = 2; i < data.length; i += 3) {
          thirdColumn.push(data[i]);
        }
      }

      // Set columns
      setColumns({
        firstColumn: firstColumn,
        secondColumn: secondColumn,
        thirdColumn: thirdColumn,
      });
    };

    // Separate data
    separateData();

    // Clean up
    return () => {
      // Log
      console.debug('🧹 Cleaning the gallery.');

      // Clean columns
      setColumns({
        firstColumn: [],
        secondColumn: [],
        thirdColumn: [],
      });
    };
  }, [data, mediumDevicesMediaQueryMatches, smallDevicesMediaQueryMatches]);

  return (
    <ContentPage>
      <Box className="media-gallery-container">
        {data.length > 0 && (
          <Box className="media-gallery-gallery-container">
            {columns.firstColumn.length > 0 && (
              <Box className="media-gallery-gallery-column-container">
                {columns.firstColumn.map((obj: EntityMedia) => {
                  return (
                    <MediaItem
                      key={`media-entity-${obj.id}`}
                      linkUrl={
                        props.baseMediaUrl.charAt(props.baseMediaUrl.length - 1) === '/'
                          ? `${props.baseMediaUrl}${obj.id}`
                          : `${props.baseMediaUrl}/${obj.id}`
                      }
                      media={obj}
                      alt={obj.description || ''}
                    />
                  );
                })}
              </Box>
            )}
            {columns.secondColumn.length > 0 && (
              <Box className="media-gallery-gallery-column-container">
                {columns.secondColumn.map((obj: EntityMedia) => {
                  return (
                    <MediaItem
                      key={`media-entity-${obj.id}`}
                      linkUrl={
                        props.baseMediaUrl.charAt(props.baseMediaUrl.length - 1) === '/'
                          ? `${props.baseMediaUrl}${obj.id}`
                          : `${props.baseMediaUrl}/${obj.id}`
                      }
                      media={obj}
                      alt={obj.description || ''}
                    />
                  );
                })}
              </Box>
            )}
            {columns.thirdColumn.length > 0 && (
              <Box className="media-gallery-gallery-column-container">
                {columns.thirdColumn.map((obj: EntityMedia) => {
                  return (
                    <MediaItem
                      key={`media-entity-${obj.id}`}
                      linkUrl={
                        props.baseMediaUrl.charAt(props.baseMediaUrl.length - 1) === '/'
                          ? `${props.baseMediaUrl}${obj.id}`
                          : `${props.baseMediaUrl}/${obj.id}`
                      }
                      media={obj}
                      alt={obj.description || ''}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        )}
        {isLoading && <LoadingContent marginTop="24px" />}
        {allLoaded && !isLoading && data.length > 0 && (
          <Typography variant="body2" className="media-gallery-footer">
            {t(translations.media_gallery.no_more_data)}
          </Typography>
        )}
        {allLoaded && !isLoading && data.length === 0 && (
          <Typography variant="body2" className="media-gallery-footer">
            {t(translations.media_gallery.without_data)}
          </Typography>
        )}
      </Box>
    </ContentPage>
  );
};
